import { createRouter, createWebHistory } from 'vue-router'

const SITE_URL = 'https://gfw-laatzen.de'
const DEFAULT_DESCRIPTION = 'Gemeinschaft Freier Wähler Laatzen – parteiunabhängig für Sicherheit, Umwelt und Schulen & Kitas in Laatzen.'

const routes = [
    {
        path: '/',
        name: 'Home',
        component: () => import('@/components/HomePage.vue'),
        meta: {
            title: 'Gemeinschaft Freier Wähler Laatzen – Gemeinsam für unsere Stadt',
            description: 'Gemeinschaft Freier Wähler Laatzen – parteiunabhängig für Sicherheit, Umwelt und Schulen & Kitas in Laatzen. Jetzt Ziele, Mitglieder und Programm entdecken.'
        }
    },
    {
        path: '/ueber-uns',
        name: 'About',
        component: () => import('@/components/AboutPage.vue'),
        meta: {
            title: 'Über uns – Gemeinschaft Freier Wähler Laatzen',
            description: 'Wer wir sind und wofür die Gemeinschaft Freier Wähler Laatzen steht: unabhängig, bürgernah und vor Ort für Laatzen engagiert.'
        }
    },
    {
        path: '/ziele',
        name: 'Ziele',
        component: () => import('@/components/ZielePage.vue'),
        meta: {
            title: 'Ziele – Gemeinschaft Freier Wähler Laatzen',
            description: 'Unsere politischen Ziele für Laatzen – konkret, unabhängig und bürgernah.'
        }
    },
    {
        path: '/mitglieder',
        name: 'Mitglieder',
        component: () => import('@/components/TeamPage.vue'),
        meta: {
            title: 'Unsere Mitglieder – Gemeinschaft Freier Wähler Laatzen',
            description: 'Lernen Sie die Mitglieder der Gemeinschaft Freier Wähler Laatzen kennen.'
        }
    },
    {
        path: '/team',
        redirect: '/mitglieder'
    },
    {
        path: '/kandidaten',
        redirect: '/mitglieder'
    },
    {
        path: '/themen',
        name: 'Themen',
        component: () => import('@/components/ThemenPage.vue'),
        meta: {
            title: 'Themen – Gemeinschaft Freier Wähler Laatzen',
            description: 'Konkrete Pläne für Sicherheit, Umwelt und Schulen & Kitas in Laatzen – unsere Themen im Detail.'
        }
    },
    {
        path: '/mitgestalten',
        name: 'Mitgestalten',
        component: () => import('@/components/MitgestaltenPage.vue'),
        meta: {
            title: 'Laatzen mitgestalten – Gemeinschaft Freier Wähler Laatzen',
            description: 'Teilen Sie Ihre Idee oder Ihren Hinweis für Laatzen mit der GFW – unbürokratisch und direkt.'
        }
    },
    {
        path: '/idee-bestaetigen',
        name: 'IdeeBestaetigen',
        component: () => import('@/components/IdeeBestaetigenPage.vue'),
        meta: {
            title: 'Idee bestätigen – Gemeinschaft Freier Wähler Laatzen',
            description: 'Bestätigen Sie Ihre eingereichte Idee für Laatzen.',
            robots: 'noindex, nofollow'
        }
    },
    {
        path: '/neuigkeiten',
        name: 'Neuigkeiten',
        component: () => import('@/components/NewsPage.vue'),
        meta: {
            title: 'Neuigkeiten – Gemeinschaft Freier Wähler Laatzen',
            description: 'Termine, Berichte und Aktionen der Gemeinschaft Freier Wähler Laatzen.'
        }
    },
    {
        path: '/impressum',
        name: 'Impressum',
        component: () => import('@/components/ImpressumPage.vue'),
        meta: {
            title: 'Impressum – Gemeinschaft Freier Wähler Laatzen',
            description: 'Impressum und Anbieterkennzeichnung der Gemeinschaft Freier Wähler Laatzen gemäß § 5 TMG.',
            robots: 'noindex, follow'
        }
    },
    {
        path: '/datenschutz',
        name: 'Datenschutz',
        component: () => import('@/components/DatenschutzPage.vue'),
        meta: {
            title: 'Datenschutzerklärung – Gemeinschaft Freier Wähler Laatzen',
            description: 'Informationen zum Umgang mit personenbezogenen Daten auf der Website der Gemeinschaft Freier Wähler Laatzen.',
            robots: 'noindex, follow'
        }
    },
    {
        path: '/transparenz',
        name: 'Transparenz',
        component: () => import('@/components/TransparenzPage.vue'),
        meta: {
            title: 'Transparenzbekanntmachung – Gemeinschaft Freier Wähler Laatzen',
            description: 'Transparenzbekanntmachung zur politischen Werbung der Gemeinschaft Freier Wähler Laatzen gemäß Verordnung (EU) 2024/900.',
            robots: 'noindex, follow'
        }
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'NotFound',
        component: () => import('@/components/NotFoundPage.vue'),
        meta: {
            title: 'Seite nicht gefunden – Gemeinschaft Freier Wähler Laatzen',
            description: 'Die aufgerufene Seite existiert nicht oder wurde verschoben.',
            robots: 'noindex, nofollow'
        }
    }
]

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior() {
        return { top: 0, behavior: 'smooth' }
    }
})

function setMetaTag(attr, key, content) {
    let el = document.querySelector(`meta[${attr}="${key}"]`)
    if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
    }
    el.setAttribute('content', content)
}

router.afterEach((to) => {
    const title = to.meta.title || 'Gemeinschaft Freier Wähler Laatzen'
    const description = to.meta.description || DEFAULT_DESCRIPTION
    const url = SITE_URL + to.path

    document.title = title

    setMetaTag('name', 'description', description)
    setMetaTag('name', 'robots', to.meta.robots || 'index, follow')
    setMetaTag('property', 'og:title', title)
    setMetaTag('property', 'og:description', description)
    setMetaTag('property', 'og:url', url)
    setMetaTag('property', 'og:type', 'website')
    setMetaTag('name', 'twitter:card', 'summary')
    setMetaTag('name', 'twitter:title', title)
    setMetaTag('name', 'twitter:description', description)

    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
        canonical = document.createElement('link')
        canonical.setAttribute('rel', 'canonical')
        document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', url)
})

export default router
